import React, { useState, useCallback } from "react";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import {
  useUpdateMany,
  useRefresh,
  useNotify,
  useUnselectAll,
  useListContext,
} from "react-admin";
import Loader from "../Loader";
import TierPrice, { convertTierPriceToString } from "./TierPrice";
import { validateTierPrice } from "./TierPrice";
import { useIsBusinessPlan } from "../../helpers/subscriptionPlan";
import { withTooltipBusinessTier } from "../../helpers/withTooltipBusinessTier";
import Checkbox from "@mui/material/Checkbox";
import { FormControlLabel, FormGroup } from "@mui/material";

const BusinessButton = withTooltipBusinessTier(Button);
const BULK_EDIT_TIERED_PRICES_LABEL = "T.price";
const BulkUpdateTierPrice = (props) => {
  const { resource, selectedIds } = useListContext();
  const [isOpen, setIsOpen] = useState(false);
  const [tierPriceStr, setTierPriceStr] = useState("");
  const [applyTieredPrices, setApplyTieredPrices] = useState(true);
  const [applyWebTieredPrices, setApplyWebTieredPrices] = useState(true);

  const isBusinessPlan = useIsBusinessPlan();
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll(resource);
  const [updateMany, { isLoading }] = useUpdateMany(
    resource,
    {
      ids: selectedIds,
      data: {
        ...(applyTieredPrices && { tieredPrices: tierPriceStr }),
        ...(applyWebTieredPrices && { webTieredPrices: tierPriceStr }),
      },
    },
    {
      onSuccess: () => {
        unselectAll();
        refresh();
        notify("Updated successfully");
        setIsOpen(false);
      },
      onFailure: (error) =>
        notify("Update tiered price failed", { type: "warning" }),
    }
  );

  const handleTieredPricesChange = (event) => {
    setApplyTieredPrices(event.target.checked);
  };

  const handleWebTieredPricesChange = (event) => {
    setApplyWebTieredPrices(event.target.checked);
  };

  const onTierPriceChange = useCallback((tierPrices) => {
    setTierPriceStr(convertTierPriceToString(tierPrices));
  }, []);

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleDialogClose = useCallback(() => {
    unselectAll(resource);
    setApplyTieredPrices(true);
    setApplyWebTieredPrices(true);
    setIsOpen(false);
  }, [unselectAll, setIsOpen, resource]);

  const handleConfirm = useCallback(() => {
    const isValid = validateTierPrice(tierPriceStr);
    if (isValid || tierPriceStr === "") {
      updateMany();
    } else {
      notify("Invalid tiered price", { type: "warning" });
    }
  }, [notify, tierPriceStr, updateMany]);
  if (!isBusinessPlan) {
    return (
      <BusinessButton disabled startIcon={<EditIcon />}>
        {BULK_EDIT_TIERED_PRICES_LABEL}
      </BusinessButton>
    );
  }

  return (
    <>
      <Button onClick={handleClick}>
        <EditIcon />
        T.price
      </Button>

      <Dialog open={isOpen} aria-labelledby="responsive-dialog-title">
        <DialogTitle>{`Set tiered price`}</DialogTitle>
        <DialogContent>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyTieredPrices}
                  onChange={handleTieredPricesChange}
                />
              }
              label="Tiered Prices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={applyWebTieredPrices}
                  onChange={handleWebTieredPricesChange}
                />
              }
              label="Webstore Tiered Prices"
            />
          </FormGroup>
          <TierPrice onChange={onTierPriceChange} />
        </DialogContent>
        <DialogActions>
          <Loader open={isLoading} />
          <Button
            variant="contained"
            disabled={
              isLoading || (!applyTieredPrices && !applyWebTieredPrices)
            }
            onClick={handleConfirm}
            color="primary"
            autoFocus
          >
            Update
          </Button>
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Loader open={isLoading}></Loader>
    </>
  );
};

export default BulkUpdateTierPrice;
